import * as _ from 'lodash';
import { useState } from 'react';
import { InjectedRouter } from 'react-router';

import { PasswordResetRequest, PasswordResetResponse } from '../../../../typings/api';
import { errorMessageFromResponse, FailedFetchResponse } from '../../../helpers/errors';
import { PasswordStrength, PasswordStrengthLabel, testPasswordStrength } from '../../../helpers/passwordStrength';
import { useInputValue } from '../../../hooks';
import { post } from '../../../redux/api';
import { RouterProps } from '../../../types';

import { AuthInput } from './AuthContext';
import useFocusInputs from './useFocusInputs';

type UsePasswordResetProps = {
  location: RouterProps['location'];
  router: InjectedRouter;
};

type UsePasswordReset = {
  email: AuthInput;
  password: AuthInput;
  hasSentResetEmail: boolean;
  isRequestPending: boolean;
  isSubmitDisabled: boolean;
  passwordStrength: PasswordStrength | null;
  onSubmit: () => Promise<void>;
  alert: PasswordResetAlert | null;
  showResendText: boolean;
};

type PasswordResetAlert = {
  message: string;
  type: 'success' | 'info' | 'error';
}

//  The first endpoint is to request the token be sent via email,
//  the 2nd actually resets the user's password.
const ENDPOINT = {
  REQUEST: '/api/auth/request-password-reset',
  RESET: '/api/auth/password-reset',
};

//  Redirect to login 2s after successfully changing password.
function redirectToLogin(router: InjectedRouter): void {
  const REDIRECT_TIMEOUT = 1000 * 2;
  _.delay(() => router.push('/auth/login'), REDIRECT_TIMEOUT);
}

/**
 * Hook for resetting password.
 *
 * Resetting the password is a two-step process. First the user enters their email and is
 * sent a password reset token. Clicking the link will send them back to this page
 * with both a token + email as query params, which are then used for the next step.
 */
const usePasswordReset = ({ location, router }: UsePasswordResetProps): UsePasswordReset => {
  const { query } = location;
  const [email, onEmailChange] = useInputValue(query.email || '');
  const [password, onPasswordChange] = useInputValue('');
  const [alert, setAlert] = useState<PasswordResetAlert | null>(null);
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  //  After successfully sending any email, we change the submit button text to say "Resend".
  const [showResendText, setShowResendText] = useState<boolean>(false);
  const { emailInputRef, passwordInputRef } = useFocusInputs();

  //  This tells us we're on "step 2" of the password reset process (ie: email has been sent).
  const hasSentResetEmail = !!(query.email);

  const passwordStrength: PasswordStrength | null = hasSentResetEmail
    ? testPasswordStrength(password)
    : null;

  const isSubmitDisabled = hasSentResetEmail
    ? password.length === 0 && passwordStrength?.label !== PasswordStrengthLabel.Excellent
    : email.length === 0;

  const onSubmit = async () => {
    //  Reset any alerts on submit.
    setAlert(null);
    setIsRequestPending(true);
    try {
      const endpoint = hasSentResetEmail
        ? ENDPOINT.RESET
        : ENDPOINT.REQUEST;

      const { data } = await post<PasswordResetRequest, PasswordResetResponse>(endpoint, {
        email,
        password: password || undefined,
      });

      if (hasSentResetEmail) {
        setAlert({
          message: data.message,
          type: 'success',
        });
        redirectToLogin(router);
      } else {
        setAlert({
          message: data.message,
          type: 'info',
        });
      }
      setShowResendText(true);
      setIsRequestPending(false);
    } catch (error) {
      setAlert({
        type: 'error',
        message: errorMessageFromResponse(error as FailedFetchResponse),
      });
      setIsRequestPending(false);
    }
  };

  return {
    email: {
      ref: emailInputRef,
      value: email,
      onChange: onEmailChange,
    },
    password: {
      ref: passwordInputRef,
      value: password,
      onChange: onPasswordChange,
    },
    hasSentResetEmail,
    isRequestPending,
    isSubmitDisabled,
    passwordStrength,
    onSubmit,
    alert,
    showResendText,
  };
};

export default usePasswordReset;
